import React, { useState, useEffect } from 'react';
import { Search, Plus, MapPin, Clock, Phone, Flag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import SEOHead from '../components/SEOHead';
import { BLOOD_TYPES } from '../mockData';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { normalizeUserData, getUserFields } from '../services/userService';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const BloodRequests = () => {
  const { user: authUser } = useAuth();
  const [requests, setRequests] = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchCity, setSearchCity] = useState('');
  const [filterCountry, setFilterCountry] = useState('all');
  const [filterBloodType, setFilterBloodType] = useState('all');
  const [filterUrgency, setFilterUrgency] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showFlagDialog, setShowFlagDialog] = useState(false);
  const [flaggingRequest, setFlaggingRequest] = useState(null);
  
  // Get user data for pre-population
  const user = authUser ? normalizeUserData(authUser) : null;
  const userFields = user ? getUserFields(user) : {};

  useEffect(() => {
    fetchRequests();
    fetchCountries();
  }, [filterCountry, searchCity, filterBloodType, filterUrgency]);

  const fetchRequests = async () => {
    try {
      const params = new URLSearchParams();
      if (filterCountry !== 'all') params.append('country', filterCountry);
      if (searchCity) params.append('city', searchCity);
      if (filterBloodType !== 'all') params.append('blood_type', filterBloodType);
      if (filterUrgency !== 'all') params.append('urgency', filterUrgency);
      params.append('status', 'active');
      
      const response = await axios.get(`${API}/blood-requests?${params.toString()}`);
      setRequests(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/regulations`);
      setAllCountries(response.data);
    } catch (error) {
      // Error handled silently - countries list will be empty if fetch fails
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'critical': return 'bg-red-700 text-white';
      case 'high': return 'bg-orange-600 text-white';
      case 'medium': return 'bg-yellow-600 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    // Client-side email validation
    const personalEmail = formData.get('personal-email');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(personalEmail)) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      await axios.post(`${API}/blood-requests`, {
        patient_name: formData.get('patient-name'),
        blood_type: formData.get('blood-type'),
        urgency: formData.get('urgency-level'),
        units_needed: parseInt(formData.get('units')),
        country: formData.get('country-select'),
        city: formData.get('city-input'),
        hospital: formData.get('hospital'),
        reason: formData.get('reason'),
        contact_phone: formData.get('contact'),
        personal_number: formData.get('personal-number'),
        patient_number: formData.get('patient-number'),
        relationship_with_patient: formData.get('relationship'),
        personal_email: personalEmail
      }, {
        params: { user_id: user?.id }
      });
      
      toast({
        title: 'Request Submitted',
        description: 'Your blood request has been submitted for admin approval.',
      });
      setShowCreateDialog(false);
      fetchRequests();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to submit request. Please try again.';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleFlagRequest = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await axios.post(`${API}/blood-requests/${flaggingRequest.id}/flag`, null, {
        params: {
          user_id: 'current-user-id', // Replace with actual user ID
          reason: formData.get('flag-reason')
        }
      });
      
      toast({
        title: 'Request Flagged',
        description: 'Thank you for helping keep our community safe.',
      });
      setShowFlagDialog(false);
      setFlaggingRequest(null);
      fetchRequests();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to flag request. Please try again.',
      });
    }
  };

  // Schema.org structured data for Blood Requests
  const requestsSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Blood Requests",
    "description": "Find urgent blood donation requests worldwide. Search by blood type, location, and urgency. Help save lives by responding to critical blood needs.",
    "url": `${process.env.REACT_APP_SITE_URL || "https://donorshouse.com"}/blood-requests`,
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": requests.length,
      "itemListElement": requests.slice(0, 10).map((request, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "MedicalCondition",
          "name": `Blood Request: ${request.blood_type}`,
          "description": `Urgent ${request.blood_type} blood needed in ${request.city}, ${request.country}`,
          "location": {
            "@type": "Place",
            "name": request.hospital,
            "address": {
              "@type": "PostalAddress",
              "addressLocality": request.city,
              "addressCountry": request.country
            }
          }
        }
      }))
    }
  };

  if (loading) {
    return (
      <>
        <SEOHead
          title="Urgent Blood Requests | Find Blood Donation Needs | DonorsHouse"
          description="Find urgent blood donation requests worldwide. Search by blood type, location, and urgency. Help save lives by responding to critical blood needs in your area."
          keywords="blood requests, urgent blood need, blood donation requests, find blood donors, blood type needed, emergency blood, critical blood request"
          schema={requestsSchema}
        />
        <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
          <div className="text-gray-700 text-lg">Loading blood requests...</div>
        </div>
      </>
    );
  }

  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://donorshouse.com';

  const breadcrumbs = [
    { name: 'Home', url: siteUrl },
    { name: 'Blood Requests', url: `${siteUrl}/blood-requests` }
  ];

  return (
    <>
      <SEOHead
        title="Urgent Blood Requests | Find Blood Donation Needs | DonorsHouse"
        description="Find urgent blood donation requests worldwide. Search by blood type, location, and urgency. Help save lives by responding to critical blood needs in your area."
        keywords="blood requests, urgent blood need, blood donation requests, find blood donors, blood type needed, emergency blood, critical blood request"
        schema={requestsSchema}
        breadcrumbs={breadcrumbs}
      />
      <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Blood Requests</h1>
          <p className="text-gray-700 text-lg">Find blood donors or post a request for blood</p>
        </div>

        {/* Filters */}
        <Card className="mb-8 shadow-md">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <Label htmlFor="search-city" className="text-gray-900 font-medium mb-2 block">Search by City</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input
                    id="search-city"
                    placeholder="Enter city name"
                    value={searchCity}
                    onChange={(e) => setSearchCity(e.target.value)}
                    className="pl-10 border-gray-300 focus:border-red-700 focus:ring-red-700"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="filter-country" className="text-gray-900 font-medium mb-2 block">Country</Label>
                <Select value={filterCountry} onValueChange={setFilterCountry}>
                  <SelectTrigger id="filter-country" className="border-gray-300">
                    <SelectValue placeholder="All Countries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {allCountries.map(country => (
                      <SelectItem key={country.code} value={country.name}>{country.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="filter-blood" className="text-gray-900 font-medium mb-2 block">Blood Type</Label>
                <Select value={filterBloodType} onValueChange={setFilterBloodType}>
                  <SelectTrigger id="filter-blood" className="border-gray-300">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {BLOOD_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="filter-urgency" className="text-gray-900 font-medium mb-2 block">Urgency</Label>
                <Select value={filterUrgency} onValueChange={setFilterUrgency}>
                  <SelectTrigger id="filter-urgency" className="border-gray-300">
                    <SelectValue placeholder="All Urgency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Urgency</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-red-700 hover:bg-red-900 text-white btn-transition">
                      <Plus className="h-4 w-4 mr-2" />
                      Post Request
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-gray-900">Create Blood Request</DialogTitle>
                      <DialogDescription className="text-gray-700">
                        Post a blood request for yourself or someone in need
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateRequest} className="space-y-4">
                      <div>
                        <Label htmlFor="patient-name" className="text-gray-900">Patient Name</Label>
                        <Input id="patient-name" name="patient-name" placeholder="Full name or Anonymous" required className="border-gray-300" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="blood-type" className="text-gray-900">Blood Type</Label>
                          <Select name="blood-type" required defaultValue={userFields.bloodType || ''}>
                            <SelectTrigger className="border-gray-300">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {BLOOD_TYPES.map(type => (
                                <SelectItem key={type} value={type}>{type}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="units" className="text-gray-900">Units Needed</Label>
                          <Input id="units" name="units" type="number" min="1" placeholder="1" required className="border-gray-300" />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="urgency-level" className="text-gray-900">Urgency Level</Label>
                        <Select name="urgency-level" required>
                          <SelectTrigger className="border-gray-300">
                            <SelectValue placeholder="Select urgency" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="critical">Critical</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="hospital" className="text-gray-900">Hospital/Clinic</Label>
                        <Input id="hospital" name="hospital" placeholder="Hospital name" required className="border-gray-300" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="city-input" className="text-gray-900">City</Label>
                          <Input 
                            id="city-input" 
                            name="city-input" 
                            placeholder="City" 
                            required 
                            className="border-gray-300"
                            defaultValue={userFields.city || ''}
                          />
                        </div>
                        <div>
                          <Label htmlFor="country-select" className="text-gray-900">Country</Label>
                          <Select name="country-select" required defaultValue={userFields.country || ''}>
                            <SelectTrigger className="border-gray-300">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {allCountries.map(country => (
                                <SelectItem key={country.code} value={country.name}>{country.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="reason" className="text-gray-900">Reason</Label>
                        <Textarea id="reason" name="reason" placeholder="Reason for blood request" rows={2} className="border-gray-300" />
                      </div>
                      <div>
                        <Label htmlFor="contact" className="text-gray-900">Contact Phone</Label>
                        <Input 
                          id="contact" 
                          name="contact" 
                          type="tel" 
                          placeholder="+1 234 567 8900" 
                          required 
                          className="border-gray-300"
                          defaultValue={userFields.phone || ''}
                        />
                      </div>
                      <div>
                        <Label htmlFor="personal-number" className="text-gray-900">Your Phone Number (Personal Number) *</Label>
                        <Input 
                          id="personal-number" 
                          name="personal-number" 
                          type="tel" 
                          placeholder="+1 234 567 8900" 
                          required 
                          className="border-gray-300"
                          defaultValue={userFields.phone || ''}
                        />
                        <p className="text-xs text-gray-600 mt-1">Your contact number as the requester</p>
                      </div>
                      <div>
                        <Label htmlFor="patient-number" className="text-gray-900">Patient Phone Number *</Label>
                        <Input id="patient-number" name="patient-number" type="tel" placeholder="+1 234 567 8900" required className="border-gray-300" />
                        <p className="text-xs text-gray-600 mt-1">Phone number of the patient needing blood</p>
                      </div>
                      <div>
                        <Label htmlFor="relationship" className="text-gray-900">Relationship with Patient *</Label>
                        <Select name="relationship" required>
                          <SelectTrigger className="border-gray-300">
                            <SelectValue placeholder="Select relationship" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Self">Self</SelectItem>
                            <SelectItem value="Family Member">Family Member</SelectItem>
                            <SelectItem value="Friend">Friend</SelectItem>
                            <SelectItem value="Colleague">Colleague</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="personal-email" className="text-gray-900">Your Email Address (Personal Email) *</Label>
                        <Input 
                          id="personal-email" 
                          name="personal-email" 
                          type="email" 
                          placeholder="your.email@example.com" 
                          required 
                          className="border-gray-300"
                          defaultValue={userFields.email || ''}
                        />
                        <p className="text-xs text-gray-600 mt-1">Your email address as the requester (will be verified)</p>
                      </div>
                      <Button type="submit" className="w-full bg-red-700 hover:bg-red-900 text-white">
                        Submit Request
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <div className="mb-4">
          <p className="text-gray-700 font-medium">
            Showing {requests.length} active request{requests.length !== 1 ? 's' : ''}
          </p>
        </div>

        {requests.length === 0 ? (
          <Card className="p-12 text-center shadow-md">
            <p className="text-gray-700 text-lg">No blood requests found matching your filters.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {requests.map((request) => (
              <Card key={request.id} className="shadow-md card-transition hover:shadow-lg">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <Badge className={getUrgencyColor(request.urgency)}>
                      {request.urgency.toUpperCase()}
                    </Badge>
                    <Badge variant="outline" className="font-bold text-red-900 border-red-900 text-lg">
                      {request.blood_type}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg text-gray-900">{request.hospital}</CardTitle>
                  <CardDescription className="text-gray-700 flex items-center">
                    <MapPin className="h-4 w-4 mr-1" />
                    {request.city}, {request.country}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-gray-700">
                  <p><strong className="text-gray-900">Patient:</strong> {request.patient_name}</p>
                  <p><strong className="text-gray-900">Needed:</strong> {request.units_needed} units</p>
                  {request.reason && <p><strong className="text-gray-900">Reason:</strong> {request.reason}</p>}
                  <div className="flex items-center text-gray-600 pt-2">
                    <Clock className="h-4 w-4 mr-1" />
                    <span className="text-xs">
                      Posted {Math.floor((new Date() - new Date(request.created_at)) / (1000 * 60 * 60 * 24))} days ago
                    </span>
                  </div>
                </CardContent>
                <CardFooter className="flex gap-2">
                  <Button className="flex-1 bg-red-700 hover:bg-red-900 text-white btn-transition">
                    <Phone className="h-4 w-4 mr-2" />
                    Contact
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFlaggingRequest(request);
                      setShowFlagDialog(true);
                    }}
                    className="text-gray-700 border-gray-700 hover:bg-gray-50"
                  >
                    <Flag className="h-4 w-4" />
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}

        {/* Flag Dialog */}
        <Dialog open={showFlagDialog} onOpenChange={setShowFlagDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gray-900">Flag Request</DialogTitle>
              <DialogDescription className="text-gray-700">
                Help us maintain quality by reporting suspicious requests
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleFlagRequest} className="space-y-4">
              <div>
                <Label htmlFor="flag-reason" className="text-gray-900">Reason for Flagging</Label>
                <Textarea
                  id="flag-reason"
                  name="flag-reason"
                  placeholder="Please describe why this request seems suspicious..."
                  rows={4}
                  required
                  className="border-gray-300"
                />
              </div>
              <p className="text-sm text-gray-600">
                Requests flagged by 3+ users are automatically hidden and reviewed by admins.
              </p>
              <Button type="submit" className="w-full bg-red-700 hover:bg-red-900 text-white">
                Submit Flag
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
    </>
  );
};

export default BloodRequests;
